import { FC } from 'react';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Reservation } from '@/types/models/Reservation';
import { Chip } from '@mui/material';
import ReservationStatus from '@/_constants/ReservationStatus.ts';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import OrderModal from '@/components/modals/Reservation/OrderModal.tsx';

type TableRow = Reservation;

type ReservationsTableProps = {
  rows: TableRow[];
  selectable?: boolean;
  onSelectedChange?: (selectedElements: number[]) => void;
  logsUrlGeneratorFn?: (reservationId: string) => string;
};

const ReservationsTable: FC<PropsWithClassName<ReservationsTableProps>> = ({
  rows,
  className,
  onSelectedChange,
  selectable = false,
  logsUrlGeneratorFn,
}) => {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    { field: 'id', headerName: t('tableLabel.id'), width: 75 },
    { field: 'client', headerName: t('tableLabel.client'), width: 150 },
    { field: 'phone', headerName: t('tableLabel.phoneNumber'), width: 150 },
    {
      field: 'date',
      headerName: t('tableLabel.date'),
      width: 200,
      cellClassName: 'flex items-center',
      renderCell: (params) => dayjs(params.row.date).format('h:mm a | MM/DD/YY'),
    },
    { field: 'people', headerName: t('tableLabel.people'), width: 100 },
    {
      field: 'status',
      headerName: t('tableLabel.status'),
      width: 150,
      renderCell: (params) => (
        <Chip
          variant="filled"
          color={
            params.row.status === ReservationStatus.BOOKED
              ? 'success'
              : params.row.status === ReservationStatus.CANCELED
                ? 'error'
                : 'warning'
          }
          label={params.row.status}
        />
      ),
    },
    {
      field: 'order',
      headerName: t('tableLabel.order'),
      width: 150,
      renderCell: (params) => (
        <Toggle
          renderTrigger={({ toggle }) => (
            <button className="text-primary-400 underline border-none bg-transparent cursor-pointer" onClick={toggle}>
              {t('components.reservationsTable.viewOrder')}
            </button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <OrderModal isOpen={isOpen} onClose={handleClose} reservationId={params.row.id} />
          )}
        />
      ),
      valueGetter: (_value, row) => row.id,
    },
    {
      field: 'call_logs',
      headerName: t('tableLabel.callLogs'),
      width: 150,
      renderCell: (params) => (
        <Link className="text-primary-400" to={logsUrlGeneratorFn ? logsUrlGeneratorFn(params.row.id) : ''}>
          {t('components.reservationsTable.viewLogs')}
        </Link>
      ),
      valueGetter: (_value, row) => row.id,
    },
  ];

  const paginationModel = { page: 0, pageSize: 10 };

  return (
    <Paper sx={{ width: '100%' }} className={className}>
      <DataGrid
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        checkboxSelection={selectable}
        onRowSelectionModelChange={(selectedIds: GridRowSelectionModel) => {
          if (onSelectedChange) {
            onSelectedChange(selectedIds.map((id) => id as number) ?? []);
          }
        }}
        disableMultipleRowSelection={true}
        initialState={{ pagination: { paginationModel } }}
        pageSizeOptions={[10, 20, 50, 100]}
        rowSelection={selectable}
        disableRowSelectionOnClick={true}
      />
    </Paper>
  );
};

export default ReservationsTable;
